import { CHAT_INPUT_EDITOR_MIN_HEIGHT_PX } from '../../constants/layout'

export interface ComposerHeights {
  contextHeight: number
  inputHeight: number
}

export interface ComposerDemand {
  /** Context's auto-fit height: the cards' height, capped at contextAutoCapPx. 0 when hidden.
   *  This is also the list's one minimum. A long draft, a window resize and dragging the handle
   *  down all stop here, so the list never shows less than it did before the user typed.
   *  Only dragging up can make the list taller than this. */
  contextDemand: number
  /** Upper bound a manual drag may grow context to. 0 when the list is hidden. */
  contextMax: number
  /** Input's natural target: the editor's unclamped content height. */
  inputDemand: number
  /** Input never shrinks below this, however little room remains. */
  inputMin: number
  /** Total vertical space available for contextHeight + inputHeight combined. */
  budget: number
}

export interface ComposerOverrides {
  /** Drag-set context height; null = follow contextDemand. Can push context bigger OR smaller
   *  than its natural demand — dragging up may reserve blank space beyond current content. */
  contextOverride: number | null
  /** Drag-set input cap; null = follow inputDemand. Only ever caps input smaller, never taller
   *  — there's no such thing as "input reserving blank space" the way context can. */
  inputCeiling: number | null
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/** Turns what ChatInput has measured into the demand that allocateComposerHeights splits up. */
export function buildComposerDemand(input: {
  isContextListVisible: boolean
  contextContentHeight: number
  contextAutoCapPx: number
  draftHtml: string
  inputContentHeight: number
  budget: number
}): ComposerDemand {
  // An empty draft is always two lines tall, whatever height the editor last reported.
  const isDraftEmpty = input.draftHtml.replace(/<[^>]*>/g, '').trim().length === 0
  const inputDemand = isDraftEmpty
    ? CHAT_INPUT_EDITOR_MIN_HEIGHT_PX
    : Math.max(CHAT_INPUT_EDITOR_MIN_HEIGHT_PX, input.inputContentHeight)

  if (!input.isContextListVisible) {
    return {
      contextDemand: 0,
      contextMax: 0,
      inputDemand,
      inputMin: CHAT_INPUT_EDITOR_MIN_HEIGHT_PX,
      budget: input.budget,
    }
  }

  // Before ContextBar reports its content height, assume the list fills its auto cap.
  const hasMeasuredContent = input.contextContentHeight > 0
  const contextDemand = hasMeasuredContent
    ? Math.min(input.contextContentHeight, input.contextAutoCapPx)
    : input.contextAutoCapPx

  return {
    contextDemand,
    contextMax: input.budget,
    inputDemand,
    inputMin: CHAT_INPUT_EDITOR_MIN_HEIGHT_PX,
    budget: input.budget,
  }
}

/**
 * Resolves context + input's actual rendered heights for the current render, given their
 * natural demands, any drag-set overrides, and the total space available.
 *
 * This is the single source of truth for composer sizing. Both the render path (ChatInput calls
 * it every render) and the drag path (applyComposerDragDelta below treats its last output as the
 * next gesture's starting point) go through it — there's no separate "auto" vs "manual" mode. An
 * override is just a target, the same as a demand is; priority kicks in uniformly whenever the
 * combined target exceeds budget, whether that happened because of a drag, a window resize, or
 * the user typing more text.
 *
 * Priority when short on space: a list dragged taller sheds back to contextDemand
 * first. Input sheds down to inputMin after that. The list never goes below contextDemand.
 */
export function allocateComposerHeights(
  demand: ComposerDemand,
  overrides: ComposerOverrides
): ComposerHeights {
  const { contextDemand, contextMax, inputDemand, inputMin, budget } = demand
  const { contextOverride, inputCeiling } = overrides

  const contextTarget = clamp(contextOverride ?? contextDemand, contextDemand, contextMax)
  // A ceiling only ever caps input smaller than its content — it can't force input taller than
  // what's actually there. Once content shrinks below a stale ceiling (e.g. text deleted), the
  // ceiling has nothing left to cap and this falls back to the demand on its own — no separate
  // "release" effect needed to un-stick it.
  const inputTarget = Math.max(
    inputMin,
    Math.min(inputCeiling ?? Number.POSITIVE_INFINITY, inputDemand)
  )

  const effectiveBudget = Number.isFinite(budget) ? budget : contextTarget + inputTarget

  const deficit = contextTarget + inputTarget - effectiveBudget
  if (deficit <= 0) {
    return { contextHeight: contextTarget, inputHeight: inputTarget }
  }

  const contextShrink = Math.min(deficit, contextTarget - contextDemand)
  const contextHeight = contextTarget - contextShrink
  const inputHeight = Math.max(inputMin, inputTarget - (deficit - contextShrink))

  return { contextHeight, inputHeight }
}

/**
 * Caps a drag-set list height at the cards' full height, so the list never shows white space
 * below its last card. Any height below that is kept.
 */
export function fitContextOverrideToCards(
  contextOverride: number | null,
  contextDemand: number,
  contextContentHeight: number,
  contextMax: number
): number | null {
  if (contextOverride === null) {
    return null
  }

  const allCardsHeight =
    contextContentHeight > 0 ? Math.min(contextContentHeight, contextMax) : contextDemand
  const snapped = Math.min(contextOverride, allCardsHeight)
  return snapped === contextDemand ? null : snapped
}

/**
 * Runs on send. Keeps the list at the height it has on screen, so the space the now-empty editor
 * hands back doesn't make the list jump.
 */
export function persistContextOverrideAfterSend(
  contextHeight: number,
  contextDemand: number
): number | null {
  return contextHeight === contextDemand ? null : contextHeight
}

/**
 * Converts one vertical drag step into new overrides, using the composer's rendered heights at
 * drag start as the baseline — a new drag always resumes from what's actually on screen, never
 * from a stale or uncapped value.
 *
 * Positive deltaY = handle dragged down = shrink; negative = dragged up = grow. Shrink sheds
 * context first (down to contextDemand), then input; grow restores input first, then context.
 */
export function applyComposerDragDelta(
  startHeights: ComposerHeights,
  deltaY: number,
  demand: ComposerDemand
): ComposerOverrides {
  let { contextHeight, inputHeight } = startHeights

  if (deltaY >= 0) {
    const contextShrink = Math.min(deltaY, Math.max(0, contextHeight - demand.contextDemand))
    contextHeight -= contextShrink
    const inputShrink = deltaY - contextShrink
    inputHeight = Math.max(demand.inputMin, inputHeight - inputShrink)
  } else {
    const growAmount = -deltaY
    const inputRestore = Math.min(growAmount, Math.max(0, demand.inputDemand - inputHeight))
    inputHeight += inputRestore

    const contextGrowAmount = growAmount - inputRestore
    const contextCeiling = Math.min(demand.contextMax, demand.budget - inputHeight)
    const contextRoom = Math.max(0, contextCeiling - contextHeight)
    contextHeight += Math.min(contextGrowAmount, contextRoom)
  }

  return {
    contextOverride: contextHeight === demand.contextDemand ? null : contextHeight,
    inputCeiling: inputHeight >= demand.inputDemand ? null : inputHeight,
  }
}
